import {
  createDefaultAuthorizationCache,
  createDefaultChainSelector,
  createDefaultWalletNotFoundHandler,
  registerMwa,
} from '@solana-mobile/wallet-standard-mobile';
import { isMobileDevice } from './mobileDetection';

let mwaRegistered = false;

/**
 * Registers the Mobile Wallet Adapter (MWA) for mobile browsers
 * This enables wallet connections from mobile browsers like Chrome on mobile devices
 * The MWA will appear as a wallet option in the wallet selection modal
 * 
 * IMPORTANT: This must be called BEFORE WalletProvider initializes
 */
export const registerMobileWalletAdapter = (): void => {
  // Only register on mobile devices and in browser environment
  if (typeof window === 'undefined' || !isMobileDevice() || mwaRegistered) {
    return;
  }

  // MWA is only supported on Android Chrome, not iOS browsers
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  
  if (isIOS) {
    // MWA doesn't work on iOS browsers, so we skip registration
    return;
  }

  try {
    // Get the app name and URL from environment or defaults
    // CRITICAL: appIdentity.uri MUST match window.location.origin exactly for WebSocket to work
    const appName = process.env.NEXT_PUBLIC_APP_NAME || 'SoulMinter';
    
    // Always use window.location.origin to ensure it matches the actual URL
    // This is critical for MWA WebSocket connections to work
    const appUrl = typeof window !== 'undefined' 
      ? window.location.origin 
      : (process.env.NEXT_PUBLIC_APP_URL || 'https://soulminter.io');
    
    // Icon should be a relative path - it will resolve to appUrl + icon
    // Per docs: "icon: 'relative/path/to/icon.png' // resolves to https://myapp.io/relative/path/to/icon.png"
    const appIcon = 'favicon.ico';

    // Get the current network and map it to MWA chain format
    // Register the Mobile Wallet Adapter
    // Following: https://docs.solanamobile.com/mobile-wallet-adapter/web-installation
    registerMwa({
      appIdentity: {
        name: appName,
        uri: appUrl,
        icon: appIcon, // Relative path - resolves to appUrl + icon
      },
      authorizationCache: createDefaultAuthorizationCache(),
      chains: ['solana:devnet', 'solana:mainnet'], // Support both, chainSelector will pick the right one
      chainSelector: createDefaultChainSelector(),
      onWalletNotFound: createDefaultWalletNotFoundHandler(),
      // Note: remoteHostAuthority is not included - only for desktop remote connections
    });

    mwaRegistered = true;
  } catch (error) {
      // Log error details
    console.error('Failed to register Mobile Wallet Adapter:', error);
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
      });
    }
  }
};

